import moment from 'moment';
import { useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router';
import { Box, Button, Column, Row, Spinner, Typography } from '@tempmee/react';
import { colors, shades, shadows } from '@tempmee/tailwind';
import {
  CHANGE_STATUS_ADJUSTMENT,
  changeStatusOfAdjustments,
  FETCH_JOB_ACTION,
  fetchJob,
} from '../../actions';
import { createLoadingSelector } from '../../api/selectors';
import {
  DisputeReasonModal,
  HygienistHeaderInfo,
  RenderLine,
} from './AdjustmentsComponents';
import { DSBackButton } from '~/components/shared/DSBackButton';
import { useAppSelector } from '~/hooks/useAppSelector';

// 3-column grid keeps back button, title, and empty column balanced.
// No DS layout primitive maps to CSS grid with column-area assignments.
const HEADER_GRID_STYLE = {
  display: 'grid',
  gridTemplateColumns: '1fr 1fr 1fr',
} as const;

// 76px top padding offsets the fixed app navbar height.
const HEADER_PADDING_TOP = 76;

// 73px indent aligns the Back button with the page left edge below the sidebar.
const BACK_BUTTON_INDENT = 73;

export default function AdjustmentScreen() {
  const params = useParams();
  const [showDisputeReasonScreenModal, setShowDisputeReasonScreenModal] = useState(false);
  const dispatch = useDispatch();
  const actions = [CHANGE_STATUS_ADJUSTMENT, FETCH_JOB_ACTION];

  const { jobId } = params;

  const job = useAppSelector((state) => state.job.job);
  const childOffices = useAppSelector((state) => state.user.childOffices);

  const currentAdjustment = useMemo(() => {
    const sortedAdjustments = job?.adjustments?.slice().sort(
      (a: { updatedAt: string }, b: { updatedAt: string }) =>
        moment(b.updatedAt).diff(moment(a.updatedAt)),
    );
    return sortedAdjustments?.filter((it: { status: string }) => it.status === 'none')?.[0] || sortedAdjustments?.[0];
  }, [job?.adjustments]);

  const isAdjustmentResolved = currentAdjustment?.status !== 'none';
  const loadingSelector = createLoadingSelector(actions);
  const isLoading = useAppSelector((state) => loadingSelector(state));

  useEffect(() => {
    dispatch(fetchJob({ jobId }));
  }, [dispatch, jobId]);

  const renderAdjustmentStatus = () => {
    if (!isAdjustmentResolved) {
      return (
        <Column style={{ alignItems: 'center', gap: 8 }}>
          <Typography color={colors.neutral[600]}>
            The professional has submitted an adjustment to this shift.
          </Typography>
          <Row gap={1}>
            <Typography bold color={colors.primary[700]}>Adjustment Difference:</Typography>
            <Typography color={colors.neutral[600]}>
              {currentAdjustment?.total_change >= 0
                ? `+$${currentAdjustment?.total_change.toFixed(2)}`
                : `-$${Math.abs(currentAdjustment?.total_change).toFixed(2)}`}
            </Typography>
          </Row>
        </Column>
      );
    }

    if (currentAdjustment?.status === 'applied') {
      return (
        <Typography color={colors.neutral[600]} style={{ textAlign: 'center' }}>
          This adjustment was submitted by the professional, and has already been reviewed and
          accepted by the office.
        </Typography>
      );
    }

    if (currentAdjustment?.status === 'declined') {
      return (
        <Typography color={colors.neutral[600]} style={{ textAlign: 'center' }}>
          This adjustment was submitted by the professional, and has already been reviewed and
          disputed. Our Adjustment Team will reach out within 24 hours and release payment
          accordingly.
        </Typography>
      );
    }

    return null;
  };

  const renderContent = () => {
    if (isLoading) {
      return (
        <Row align="center" justify="center" style={{ width: '100%', flex: 1, minHeight: 300 }}>
          <Spinner />
        </Row>
      );
    }
    if (!job) {
      return null;
    }
    return (
      <Column style={{ alignItems: 'center', width: '50%', maxWidth: 700, minWidth: 500 }}>
        {childOffices.length !== 0 && (
          <RenderLine title="Office:" value={job.offer_owner?.office_name} />
        )}
        <RenderLine title="Date:" value={job.local_date} />
        <RenderLine
          title="Hours:"
          value={`${job.local_start_time} - ${job.local_end_time}`}
          isOldFied
          timezone
        />
        <RenderLine
          title="Adjusted Hours:"
          value={`${moment
            .tz(currentAdjustment?.start, job?.offer_owner?.time_zone)
            .format('hh:mm a')} - ${moment
            .tz(currentAdjustment?.finish, job?.offer_owner?.time_zone)
            .format('hh:mm a')}`}
          isAdjustedField
        />
        <RenderLine title="Hourly Rate:" value={`$${job.rate.toFixed(2)}`} />
        <RenderLine
          title={`Meal Break ${job.is_lunch_break_paid ? '(Paid)' : '(Unpaid)'}`}
          value={`${job.lunch_break_time} min`}
          isOldFied
        />
        <RenderLine
          title="Adjusted Meal Break:"
          value={`${currentAdjustment?.break} Min`}
          isAdjustedField
        />
        <RenderLine title="Auto-Confirm:" value={job.autoFill ? 'On' : 'Off'} />

        <Box style={{ marginTop: 64, marginBottom: 64, textAlign: 'center' }}>
          {renderAdjustmentStatus()}
        </Box>

        {!isAdjustmentResolved && (
          <Row gap={6} style={{ marginBottom: 70 }}>
            <Button
              variant="tertiary"
              style={{ maxWidth: 200 }}
              onClick={() => setShowDisputeReasonScreenModal(true)}
            >
              Dispute
            </Button>
            <Button
              variant="primary"
              style={{ maxWidth: 200 }}
              onClick={() =>
                dispatch(
                  changeStatusOfAdjustments({
                    jobId,
                    adjustmentId: currentAdjustment.id,
                    status: 'applied',
                  }),
                )
              }
            >
              Confirm
            </Button>
          </Row>
        )}
      </Column>
    );
  };

  return (
    <Column style={{ alignItems: 'center', flex: 1, minHeight: '100vh' }}>
      <Box
        style={{
          ...HEADER_GRID_STYLE,
          backgroundColor: shades[0],
          width: '100%',
          paddingTop: HEADER_PADDING_TOP,
          paddingBottom: 35,
          boxShadow: shadows['elevation-4'],
          zIndex: 2,
          position: 'relative',
          marginBottom: 64,
        }}
      >
        <Box style={{ gridArea: '1 / 1', paddingLeft: BACK_BUTTON_INDENT }}>
          <DSBackButton />
        </Box>
        <Column style={{ gridArea: '1 / 2', alignItems: 'center' }}>
          <Typography
            size="heading_2"
            bold
            color={colors.neutral[600]}
            style={{ textAlign: 'center', width: '100%' }}
          >
            New Adjustments
          </Typography>
          <HygienistHeaderInfo job={job} />
        </Column>
      </Box>
      {renderContent()}
      {showDisputeReasonScreenModal && (
        <DisputeReasonModal
          submitDisputeReasonMessage={(data) =>
            dispatch(
              changeStatusOfAdjustments({
                jobId,
                adjustmentId: currentAdjustment.id,
                disputeReason: data,
                status: 'declined',
              }),
            )
          }
          closeModal={() => setShowDisputeReasonScreenModal(false)}
        />
      )}
    </Column>
  );
}
